import { css, styled, themeType } from '../../hooks/theme';
import { getCssFont } from '../../styles';
import { ESize, Size, Tokens } from '../../types';

interface BaseTextFieldProps {
  width?: string | number;
}

export const BaseTextField = styled.div<BaseTextFieldProps>`
  display: inline-flex;
  flex-direction: column;
  position: relative;
  width: ${props => props.width ?? props.width};
  min-width: 0px;
  padding: 0px;
  margin: 0px;
  border: 0px;
  vertical-align: top;
  background: ${props => props.theme.colors.neutral.grey[0]};
  border-radius: ${Tokens.inputs.textField.border.radius};

  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type='number'] {
    -moz-appearance: textfield;
  }

  svg {
    margin-right: 0.5rem;
  }
`;

BaseTextField.defaultProps = {
  theme: Tokens.eixods,
};

interface TextFieldOutlinedProps {
  size?: Size;
  hasPlaceholder?: boolean;
  error?: boolean;
  disabled?: boolean;
  rows?: number;
  multiline?: boolean;
  isFocused: boolean;
}

const getCssBasicInput = (props: TextFieldOutlinedProps & { theme: themeType }) => {
  return css`
    font: inherit;
    letter-spacing: inherit;
    color: ${props.theme.colors.neutral.grey[100]};
    border: 0px;
    box-sizing: content-box;
    background: none;
    height: ${props.multiline ? props.rows : 1}rem;
    margin: 0px;
    -webkit-tap-highlight-color: transparent;
    display: block;
    min-width: 0px;
    width: 100%;
    animation-name: mui-auto-fill-cancel;
    animation-duration: 10ms;
    padding: ${Tokens.inputs.textField.size[props.size] ?? Tokens.inputs.textField.size.lg};

    &:focus {
      outline: 0px;
    }

    ${props.disabled &&
    css`
      color: ${props.theme.colors.neutral.grey[40]};
      cursor: not-allowed;
    `}

    ${() => {
      const transform =
        Tokens.inputs.textField.transform[props.size] ??
        Tokens.inputs.textField.transform[ESize.large];
      return css`
        &:focus + label {
          max-width: calc(133% - 24px);
          transform: translate(${transform}) scale(0.75);
          pointer-events: auto;
          user-select: none;
          font-size: ${props => Number(props.theme.font.size.xs) / 16}rem;
          color: ${props.error
            ? props.theme.colors.feedback.error
            : props.theme.colors.neutral.grey[100]};
        }

        :not(:placeholder-shown):not(:focus) + label {
          max-width: calc(133% - 24px);
          font-size: ${props => Number(props.theme.font.size.xs) / 16}rem;
          transform: translate(${transform}) scale(0.75);
          pointer-events: auto;
          user-select: none;
        }
      `;
    }}


  &:focus ~ fieldset legend {
      float: unset;
      overflow: hidden;
      display: block;
      width: auto;
      padding: 0px;
      height: 11px;
      font-size: 12px;
      visibility: hidden;
      ${props.hasPlaceholder &&
      css`
        max-width: 100%;
      `}
      transition: max-width 100ms cubic-bezier(0, 0, 0.2, 1) 50ms;
      white-space: nowrap;
    }

    &:focus ~ fieldset {
      border-color: ${props.error
        ? props.theme.colors.feedback.error
        : props.theme.colors.neutral.grey[100]};
    }

    :not(:placeholder-shown):not(:focus) ~ fieldset legend {
      float: unset;
      overflow: hidden;
      display: block;
      width: auto;
      padding: 0px;
      height: 11px;
      font-size: 12px;
      visibility: hidden;
      ${props.hasPlaceholder &&
      css`
        max-width: 100%;
      `}
      transition: max-width 100ms cubic-bezier(0, 0, 0.2, 1) 50ms;
      white-space: nowrap;
    }
  `;
};

export const TextFieldOutlined = styled.div<TextFieldOutlinedProps>`
  ${props => css`
    ${getCssFont(props.theme.font.desktop.paragraph.p2.regular)}
    color: ${props.theme.colors.neutral.grey[40]};
    background: ${props.disabled && props.theme.colors.neutral.grey[5]};
  `}
  box-sizing: border-box;
  cursor: text;
  display: inline-flex;
  -webkit-box-align: center;
  align-items: center;
  position: relative;
  border-radius: ${Tokens.inputs.textField.border.radius};

  ${props =>
    props.isFocused &&
    css`
      color: ${props.theme.colors.neutral.grey[100]};
    `}

  ${props =>
    props.disabled &&
    css`
      cursor: not-allowed;
    `}

  &:hover {
    color: ${props => !props.disabled && props.theme.colors.neutral.grey[100]};
    ${props => {
      const color = props.theme.colors.neutral.grey[100];
      return (
        !props.error &&
        !props.disabled &&
        css`
          & label {
            color: ${color};
          }
          & fieldset {
            border-color: ${color};
            color: ${color};
          }
        `
      );
    }}
  }

  input {
    ${props => getCssBasicInput(props)}
  }

  textarea {
    ${props => getCssBasicInput(props)}

    &::-webkit-scrollbar-track {
      box-shadow: inset 0 0 6px rgba(0, 0, 0, 0);
      border-radius: ${Tokens.inputs.textField.border.radius};
      background-color: transparent;
    }

    &::-webkit-scrollbar {
      width: 6px;
      background-color: transparent;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: ${Tokens.inputs.textField.border.radius};
      box-shadow: inset 0 0 6px rgba(0, 0, 0, 0);
      background-color: ${props => props.theme.colors.neutral.grey[10]};
    }
  }

  label {
    ${props => getCssFont(props.theme.font.desktop.paragraph.p2.regular)}
    color: ${props =>
      props.error && !props.disabled
        ? props.theme.colors.feedback.error
        : props.theme.colors.neutral.grey[70]};
    padding: 0px;
    display: block;
    transform-origin: left top;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: calc(100% - 24px);
    position: absolute;
    left: 2px;
    ${props => {
      switch (props.size) {
        case ESize.small:
          return css`
            top: -8.5px;
          `;
        case ESize.large:
          return css`
            top: -2.5px;
          `;
        default:
          return css`
            top: -2.5px;
          `;
      }
    }}
    transform: translate(14px, 16px) scale(1);
    transition: color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms,
      transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms, max-width 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
    pointer-events: none;
  }

  fieldset {
    text-align: left;
    position: absolute;
    inset: -5px 0px 0px;
    margin: 0px;
    padding: 0px 8px;
    pointer-events: none;
    border-radius: inherit;
    border-style: solid;
    border-width: 1px;
    overflow: hidden;
    min-width: 0%;
    border-color: ${props =>
      props.error && !props.disabled
        ? props.theme.colors.feedback.error
        : props.theme.colors.neutral.grey[40]};

    legend {
      float: unset;
      overflow: hidden;
      display: block;
      width: auto;
      padding: 0px;
      height: 11px;
      visibility: hidden;
      max-width: 0.01px;
      transition: max-width 50ms cubic-bezier(0, 0, 0.2, 1) 0ms;
      white-space: nowrap;

      & > span {
        padding-left: 4px;
        padding-right: 4px;
        display: inline-block;
        opacity: 0;
        visibility: visible;
        font-size: 0.5625rem;
      }
    }
  }
`;

TextFieldOutlined.defaultProps = {
  theme: Tokens.eixods,
};

interface SupportingTextProps {
  error?: boolean;
  disabled?: boolean;
}
export const SupportingText = styled.span<SupportingTextProps>`
  ${props => css`
    ${getCssFont(props.theme.font.desktop.paragraph.p3.regular)}
    color: ${(props.disabled && props.theme.colors.neutral.grey[40]) ||
    (props.error && props.theme.colors.feedback.error) ||
    props.theme.colors.neutral.grey[100]};
  `}

  display: block;
  position: absolute;
  bottom: -18px;
`;

SupportingText.defaultProps = {
  theme: Tokens.eixods,
};
